import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography, Select, MenuItem, CircularProgress, Tooltip, Avatar, Paper, Grid, useTheme, useMediaQuery, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSchedules, updateSchedules, cancelClassSignUp, signUpForClass } from '../../features/schedules/schedulesSlice';
import { 
  format, 
  parseISO, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval,
  isToday,
  isSameMonth,
  addMonths,
  subMonths,
} from 'date-fns';
import { createGoogleCalendarUrl } from '../../utils/calendarUtils';
import { useLocation } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { alpha } from '@mui/material/styles';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function Calendar() {
  const dispatch = useDispatch();
  const { schedules, loading, error } = useSelector(state => state.schedules);
  const { user } = useSelector(state => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const today = new Date();
  const dayRefs = useRef([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [viewMode, setViewMode] = useState('evening'); // 'all', 'evening', 或 'personal'
  const [highlightedDay, setHighlightedDay] = useState(null);
  const location = useLocation();
  const [signedUpClasses, setSignedUpClasses] = useState({});

  // Define highlight animation styles BEFORE using it
  const highlightAnimation = keyframes`
    0% { background-color: rgba(33, 136, 240, 0.9) }  // 使用 Material-UI 的 red[700]
    100% { background-color: transparent }
  `;

  useEffect(() => {
    dispatch(fetchSchedules());
  }, [dispatch]);

  // Initialize with current date
  useEffect(() => {
    const currentDate = new Date();
    setYear(currentDate.getFullYear());
    setSelectedMonth(currentDate.getMonth());
  }, []);

  // Scroll to current date after loading
  useEffect(() => {
    if (!loading) {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentDay = currentDate.getDate();
      
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        scrollToDay(currentMonth, currentDay);
      }, 100);
    }
  }, [loading, year]); // Add year as dependency

  // Parse highlight parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const highlight = searchParams.get('highlight');
    
    if (highlight) {
      const [year, month, day] = highlight.split('-').map(Number);
      
      // 立即设置状态和高亮
      setYear(year);
      setSelectedMonth(month - 1);
      setHighlightedDay({ year, month: month - 1, day });
      
      // 减少跳转延迟时间
      requestAnimationFrame(() => {
        scrollToDay(month - 1, day);
      });
      
      // 延长高亮动画时间到 10 秒
      setTimeout(() => {
        setHighlightedDay(null);
        window.history.replaceState({}, '', '/schedule');
      }, 10000);
    }
  }, [location.search]);

  // 添加一个 useEffect 来初始化 signedUpClasses
  useEffect(() => {
    if (schedules.length > 0) {
      const initialSignedUpState = {};
      schedules.forEach(schedule => {
        if (schedule.is_signed_up) {
          initialSignedUpState[schedule.id] = true;
        }
      });
      setSignedUpClasses(initialSignedUpState);
    }
  }, [schedules]);

  const scrollToDay = (monthIndex, dayIndex) => {
    const targetDayIndex = dayRefs.current.findIndex(
      (ref) => ref && 
      ref.getAttribute('data-month') === `${monthIndex}` && 
      ref.getAttribute('data-day') === `${dayIndex}`
    );

    const targetElement = dayRefs.current[targetDayIndex];

    if (targetDayIndex !== -1 && targetElement) {
      const container = document.querySelector('.calendar-container');
      if (container) {
        const elementRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offset = elementRect.top - containerRect.top - (containerRect.height / 2.5);

        container.scrollTo({
          top: container.scrollTop + offset,
          behavior: 'smooth',
        });
      }
    }
  };

  const handlePrevYear = () => setYear(prev => prev - 1);
  const handleNextYear = () => setYear(prev => prev + 1);

  const handleMonthChange = (event) => {
    const monthIndex = parseInt(event.target.value, 10);
    setSelectedMonth(monthIndex);
    scrollToDay(monthIndex, 1);
  };

  const handleTodayClick = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    // Update both year and month
    setYear(currentYear);
    setSelectedMonth(currentMonth);

    // Wait for state updates to complete before scrolling
    setTimeout(() => {
      scrollToDay(currentMonth, currentDay);
    }, 100);
  };

  const handleUpdateSchedules = async () => {
    if (!user?.is_superuser) {
      alert("Only administrators can update schedules");
      return;
    }

    try {
      setUpdating(true);
      await dispatch(updateSchedules()).unwrap();
      await dispatch(fetchSchedules()).unwrap();
    } catch (error) {
      console.error('Failed to update schedules:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleSignUp = async (event, classId) => {
    event.stopPropagation();
    try {
      if (signedUpClasses[classId]) {
        // Optimistic update for cancel
        setSignedUpClasses(prev => {
          const newState = { ...prev };
          delete newState[classId];
          return newState;
        });
        
        // API call
        await dispatch(cancelClassSignUp(classId)).unwrap();
        
        // Refresh schedules to get updated state
        await dispatch(fetchSchedules()).unwrap();
      } else {
        // Optimistic update for sign up
        setSignedUpClasses(prev => ({
          ...prev,
          [classId]: true
        }));
        
        // API call
        await dispatch(signUpForClass(classId)).unwrap();
        
        // Refresh schedules to get updated state
        await dispatch(fetchSchedules()).unwrap();
      }
    } catch (error) {
      // Revert optimistic update on error
      if (signedUpClasses[classId]) {
        setSignedUpClasses(prev => ({
          ...prev,
          [classId]: true
        }));
      } else {
        setSignedUpClasses(prev => {
          const newState = { ...prev };
          delete newState[classId];
          return newState;
        });
      }
      console.error('Failed to handle sign up:', error);
    }
  };

  const getClassesForDay = (day, month) => {
    return schedules.filter(schedule => {
      // First check if the date matches
      const dateMatches = parseInt(schedule.day) === day && 
                         parseInt(schedule.month) === month + 1 && 
                         parseInt(schedule.year) === year;
      
      if (!dateMatches) return false;

      // For personal view, only show signed up classes
      if (viewMode === 'personal') {
        return schedule.is_signed_up;
      }

      // For 'all' mode, show all classes
      if (viewMode === 'all') return true;

      // For 'evening' mode:
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      const hour = parseInt(schedule.time.split(':')[0]);
      
      // Show all classes on Saturday OR evening classes (after 1pm) on weekdays
      return dayOfWeek === 6 || hour >= 13;
    });
  };

  // 添加一个辅助函数来获取用户名缩写
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);  // 只取前两个字母
  };

  // 首先在组件内部定义一个可重用的按钮组件
  const ActionButtons = ({ schedule, handleSignUp, signedUpClasses }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {/* Sign Up Button */}
      <Tooltip title={schedule.is_signed_up ? "Cancel Sign Up" : "Sign Up"}>
        <IconButton 
          size="small" 
          sx={{ 
            color: 'white', 
            p: 0.25,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleSignUp(e, schedule.id);
          }}
        >
          {schedule.is_signed_up ? (
            <RemoveIcon sx={{ fontSize: '1.2rem' }} />
          ) : (
            <AddIcon sx={{ fontSize: '1.2rem' }} />
          )}
        </IconButton>
      </Tooltip>

      {/* Google Calendar Button */}
      <Tooltip title="Add to Google Calendar">
        <IconButton 
          size="small" 
          sx={{ 
            color: 'white', 
            p: 0.25,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(createGoogleCalendarUrl(schedule), '_blank');
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Tooltip>

      {/* Info/Techniques Button */}
      <Tooltip title="View Techniques">
        <IconButton 
          size="small" 
          sx={{ 
            color: 'white', 
            p: 0.25,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/techniques?class=${schedule.class_number}&scroll=true`;
          }}
        >
          <InfoIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // 然后在桌面视图的 formatClassInfo 函数中使用
  const formatClassInfo = (event) => {
    // Extract class number and technique names
    const classNumberIndex = event.class_name.indexOf(' - ');
    const classInfo = event.class_name.substring(0, classNumberIndex);
    const techniqueNames = event.class_name.substring(classNumberIndex + 3);
    
    // Format time range
    const [hours, minutes] = event.time.split(':').map(Number);
    const endTime = `${(hours + 1).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const timeRange = `${event.time} - ${endTime}`;
    
    // Split technique names if there are multiple
    const techniques = techniqueNames ? techniqueNames.split(' || ') : [];
    
    const bgColor = getClassColor(event.time, new Date(event.year, event.month - 1, event.day));

    return (
      <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: bgColor, color: 'white' }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          pb: 0.5,
          mb: 0.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              {classInfo}
            </Typography>
            {/* Show all signed up users */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {event.signed_up_users.map((signedUser) => (
                <Tooltip key={signedUser.id} title={signedUser.username}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      color: 'white',
                    }}
                  >
                    {getInitials(signedUser.username)}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>
          <ActionButtons 
            schedule={event}
            handleSignUp={handleSignUp}
            signedUpClasses={signedUpClasses}
          />
        </Box>

        {/* Second line: Time */}
        <Typography variant="caption" sx={{ 
          display: 'block',
          fontWeight: 800,
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          pb: 0.5,
          mb: 0.5
        }}>
          {timeRange}
        </Typography>

        {/* Techniques */}
        {techniques.map((technique, index) => (
          <Typography 
            key={index}
            variant="caption" 
            sx={{ 
              display: 'block',
              wordWrap: 'break-word',
              lineHeight: 1.2,
              ...(index < techniques.length - 1 && {
                borderBottom: '1px dashed rgba(255,255,255,0.1)',
                pb: 0.5,
                mb: 0.5
              })
            }}
          >
            {technique}
          </Typography>
        ))}
      </Box>
    );
  };

  const getClassColor = (time, date) => {
    // Get the day of week (0 = Sunday, 6 = Saturday)
    const dayOfWeek = date.getDay();
    
    // If it's Saturday, use blue color with 50% opacity
    if (dayOfWeek === 6) {
      return 'rgba(25, 118, 210, 0.7)';  // #1976d2 with 50% opacity
    }
    
    // Otherwise, use existing color logic with 50% opacity
    const hour = parseInt(time.split(':')[0]);
    return hour < 13 
      ? 'rgba(76, 175, 80, 0.7)'   // #4caf50 (green) with 50% opacity
      : 'rgba(245, 124, 0, 0.7)';  // #f57c00 (orange) with 50% opacity
  };

  // 添加一个辅助函数来格式化日期显示
  const formatDayDisplay = (day, month) => {
    if (day === 1) {
      // 使用 format 函数获取月份缩写
      const date = new Date(year, month, day);
      return format(date, 'MMM d');
    }
    return day;
  };

  const generateCalendar = useMemo(() => {
    const getDaysInMonth = () => {
      const days = [];
      const firstDayOfMonth = new Date(year, selectedMonth, 1);
      const lastDayOfMonth = new Date(year, selectedMonth + 1, 0);
      const startDayOfWeek = firstDayOfMonth.getDay();
      const daysInMonth = lastDayOfMonth.getDate();

      // Add days from previous month if needed
      // Adjust for Monday start (0 = Monday, 6 = Sunday)
      const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
      if (adjustedStartDay > 0) {
        const prevMonth = selectedMonth - 1;
        const prevMonthLastDay = new Date(year, selectedMonth, 0).getDate();
        for (let i = adjustedStartDay - 1; i >= 0; i--) {
          days.push({ 
            month: prevMonth, 
            day: prevMonthLastDay - i 
          });
        }
      }

      // Add days of current month
      for (let day = 1; day <= daysInMonth; day++) {
        const dayOfWeek = new Date(year, selectedMonth, day).getDay();
        if (dayOfWeek !== 0) {  // Skip Sundays
          days.push({ 
            month: selectedMonth, 
            day 
          });
        }
      }

      // Add days from next month to complete the grid
      const remainingDays = 6 - (days.length % 6);  // Changed from 7 to 6
      if (remainingDays < 6) {  // Changed from 7 to 6
        const nextMonth = selectedMonth + 1;
        for (let day = 1; day <= remainingDays; day++) {
          const dayOfWeek = new Date(year, nextMonth, day).getDay();
          if (dayOfWeek !== 0) {  // Skip Sundays
            days.push({ 
              month: nextMonth, 
              day 
            });
          }
        }
      }

      return days;
    };

    const calendarDays = getDaysInMonth();
    const calendarWeeks = [];
    
    for (let i = 0; i < calendarDays.length; i += 6) {
      calendarWeeks.push(calendarDays.slice(i, i + 6));
    }

    return calendarWeeks.map((week, weekIndex) => (
      <Box 
        key={`week-${weekIndex}`}
        sx={{ 
          display: 'flex', 
          width: '100%' 
        }}
      >
        {week.map(({ month, day }, dayIndex) => {
          const index = weekIndex * 6 + dayIndex;
          const isCurrentMonth = month === selectedMonth;
          const isToday = today.getMonth() === month && 
                         today.getDate() === day && 
                         today.getFullYear() === year;
          
          const dayClasses = getClassesForDay(day, month);

          return (
            <Box
              key={`${month}-${day}`}
              ref={el => { dayRefs.current[index] = el; }}
              data-month={month}
              data-day={day}
              sx={{
                position: 'relative',
                aspectRatio: '1',
                width: '100%',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 1,
                cursor: 'pointer',
                bgcolor: isCurrentMonth ? 'background.paper' : 'grey.100',
                '&:hover': {
                  borderColor: 'primary.main',
                  zIndex: 1
                },
                ...(isToday && {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }),
                ...(highlightedDay && 
                  highlightedDay.year === year && 
                  highlightedDay.month === month && 
                  highlightedDay.day === day && {
                    animation: `${highlightAnimation} 10s ease-out`
                  })
              }}
            >
              <Typography
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isCurrentMonth ? 'text.primary' : 'text.secondary'
                }}
              >
                {formatDayDisplay(day, month)}
              </Typography>

              <Box sx={{ mt: 4 }}>
                {dayClasses.map((event, eventIndex) => (
                  <Box
                    key={eventIndex}
                    sx={{
                      bgcolor: getClassColor(event.time, new Date(event.year, event.month - 1, event.day)),
                      color: 'white',
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      '&:hover': {
                        opacity: 0.9,
                      },
                      mb: 0.5
                    }}
                  >
                    {formatClassInfo(event)}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    ));
  }, [year, selectedMonth, schedules, viewMode, highlightedDay, highlightAnimation]); // Add highlightAnimation to dependencies

  // Add year options (e.g., current year ± 5 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  }, []);

  // Update year handler
  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value, 10);
    setYear(newYear);
  };

  // Add view mode handler
  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  const formatDate = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: isMobile ? 'short' : 'long',
      month: isMobile ? 'numeric' : 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTimeRange = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = (hours + 1) % 24;
    return `${startTime} - ${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleClassInfoClick = (classEvent, e) => {
    window.location.href = `/techniques?class=${classEvent.class_number}&scroll=true`;
  };

  return (
    <Box className="calendar-container" sx={{ 
      maxHeight: '100%',
      overflow: 'auto',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 3,
      pb: 2
    }}>
      {/* Calendar Header and Grid View */}
      {!isMobile && (
        <>
          <Box sx={{ 
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            p: 2
          }}>
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Month Dropdown */}
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  {monthNames.map((month, index) => (
                    <MenuItem key={index} value={index}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>

                {/* Year Dropdown */}
                <Select
                  value={year}
                  onChange={handleYearChange}
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  {yearOptions.map((yearOption) => (
                    <MenuItem key={yearOption} value={yearOption}>
                      {yearOption}
                    </MenuItem>
                  ))}
                </Select>

                {/* View Mode Dropdown */}
                <Select
                  value={viewMode}
                  onChange={handleViewModeChange}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="evening">Evening & Saturday Classes</MenuItem>
                  <MenuItem value="all">All Classes</MenuItem>
                  <MenuItem value="personal">My Sign-ups Only</MenuItem>
                </Select>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Google Drive Button */}
                <Tooltip title="View Calendar PDFs">
                  <IconButton 
                    size="small"
                    onClick={() => window.open('https://drive.google.com/drive/folders/1F472jhnZ9IiXDhYZPgobKx-p99TLTCmc', '_blank')}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <DriveFileMoveIcon />
                  </IconButton>
                </Tooltip>

                {/* Refresh Button */}
                <Tooltip title={user?.is_superuser ? 
                  "Update schedules from PDF" : 
                  "Only administrators can update schedules"
                }>
                  <span>
                    <IconButton 
                      onClick={handleUpdateSchedules}
                      disabled={updating || !user?.is_superuser}
                      size="small"
                      sx={{
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        }
                      }}
                    >
                      <RefreshIcon sx={{ 
                        animation: updating ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Days of week header - now 6 columns */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',  // Changed from 7 to 6
              borderBottom: 1,
              borderColor: 'grey.300'
            }}>
              {daysOfWeek.map(day => (
                <Box key={day} sx={{ 
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: 'text.secondary'
                }}>
                  {day}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            {generateCalendar}
          </Box>
          <Box sx={{ 
            mt: 2, 
            p: 1, 
            display: 'flex', 
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {viewMode === 'all' && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  width: 16, 
                  height: 16, 
                  bgcolor: '#4caf50', // darker green
                  borderRadius: 0.5, 
                  mr: 0.5 
                }} />
                <Typography variant="body2">Morning Class</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#f57c00', // darker orange
                borderRadius: 0.5, 
                mr: 0.5 
              }} />
              <Typography variant="body2">Evening Class</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#1976d2', // blue
                borderRadius: 0.5, 
                mr: 0.5 
              }} />
              <Typography variant="body2">Saturday Class</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Mobile List View */}
      {isMobile && (
        <Box sx={{ p: 2 }}>
          {/* Header with buttons */}
          <Box sx={{ 
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            pb: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="h5" component="h1">
                Schedule
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Google Drive Button */}
                <Tooltip title="View Calendar PDFs">
                  <IconButton 
                    size="small"
                    onClick={() => window.open('https://drive.google.com/drive/folders/1F472jhnZ9IiXDhYZPgobKx-p99TLTCmc', '_blank')}
                  >
                    <DriveFileMoveIcon />
                  </IconButton>
                </Tooltip>

                {/* Refresh Button */}
                <Tooltip title={user?.is_superuser ? 
                  "Update schedules from PDF" : 
                  "Only administrators can update schedules"
                }>
                  <IconButton 
                    onClick={handleUpdateSchedules}
                    disabled={updating}
                    size="small"
                  >
                    <RefreshIcon sx={{ 
                      animation: updating ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* View Mode Filter */}
            <Select
              value={viewMode}
              onChange={handleViewModeChange}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            >
              <MenuItem value="evening">Evening & Saturday Classes</MenuItem>
              <MenuItem value="all">All Classes</MenuItem>
              <MenuItem value="personal">My Sign-ups Only</MenuItem>
            </Select>
          </Box>

          {/* Class Cards */}
          <Grid container spacing={2}>
            {schedules
              .filter(schedule => {
                // Only show future classes
                const scheduleDate = new Date(schedule.year, schedule.month - 1, schedule.day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (scheduleDate < today) return false;

                // Apply view mode filter
                if (viewMode === 'personal') return schedule.is_signed_up;
                if (viewMode === 'evening') {
                  const hour = parseInt(schedule.time.split(':')[0]);
                  const dayOfWeek = scheduleDate.getDay();
                  return dayOfWeek === 6 || hour >= 13;
                }
                return true;
              })
              .map((schedule) => (
                <Grid item xs={12} key={schedule.id}>
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      bgcolor: getClassColor(schedule.time, new Date(schedule.year, schedule.month - 1, schedule.day))
                    }}
                  >
                    {/* Header with class info and action buttons */}
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      color: 'white'
                    }}>
                      {/* Left side: Date and time */}
                      <Box>
                        <Typography variant="h6">
                          {format(new Date(schedule.year, schedule.month - 1, schedule.day), 'EEE, M/d')}
                        </Typography>
                        <Typography variant="body1">
                          {formatTimeRange(schedule.time)}
                        </Typography>
                      </Box>

                      {/* Right side: Action buttons */}
                      <ActionButtons 
                        schedule={schedule}
                        handleSignUp={handleSignUp}
                        signedUpClasses={signedUpClasses}
                      />
                    </Box>

                    {/* Class name with bullet points */}
                    <Box sx={{ color: 'white' }}>
                      {/* Extract and format class name */}
                      {(() => {
                        const parts = schedule.class_name.split(' - ');
                        const classNumber = parts[0];
                        const techniques = parts.slice(1).join(' - ').split(' || ');
                        
                        return (
                          <>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 'bold',
                                mb: 1
                              }}
                            >
                              {classNumber}
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              {techniques.map((technique, index) => (
                                <Box 
                                  key={index} 
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.5
                                  }}
                                >
                                  <Box 
                                    sx={{ 
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      bgcolor: 'white',
                                      mr: 1,
                                      flexShrink: 0
                                    }} 
                                  />
                                  <Typography variant="body2">
                                    {technique}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </>
                        );
                      })()}
                    </Box>

                    {/* Signed up users */}
                    {schedule.signed_up_users.length > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        {schedule.signed_up_users.map((user, index) => (
                          <Tooltip 
                            key={index} 
                            title={user.username}
                            placement="top"
                            arrow
                            PopperProps={{
                              sx: {
                                '& .MuiTooltip-tooltip': {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  boxShadow: 1,
                                  p: 1,
                                  fontSize: '0.875rem'
                                }
                              }
                            }}
                          >
                            <Avatar 
                              src={user.avatar}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                border: '2px solid rgba(255, 255, 255, 0.5)',
                                cursor: 'pointer'
                              }}
                            >
                              {getInitials(user.username)}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
          </Grid>

          {schedules.length === 0 && !loading && (
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                mt: 4,
                color: theme.palette.text.secondary
              }}
            >
              No classes scheduled.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default Calendar; 